<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { Mail, Loader2, Phone } from 'lucide-vue-next';
import { getAPI } from '@/lib/api';
import { i18n, countryCodes } from '@/i18n';
import { cn } from '@/lib/utils';
import { useRoute } from 'vue-router';
import { useToast } from '@/components/ui/toast';
import Input from '@/components/auth/Input.vue';
import { setToken } from '@/lib/user';

const { toast } = useToast();
const route = useRoute();
const props = defineProps({
  class: String,
  loginType: String,
  showEmail: Boolean,
  showPhone: Boolean,
  showCase: {
    type: Boolean,
    default: true,
  },
  afterAuth: Function,
});

const emit = defineEmits([
  'update:login-type',
  'scroll-to-bottom',
  'update:show-email',
  'update:show-phone',
]);
const isLogin = computed({
  get: () => props.loginType === 'sign-in',
  set: (v) => emit('update:login-type', v ? 'sign-in' : 'sign-up'),
});

const emailInputRef = ref(null);
const phoneInputRef = ref(null);
const isLoading = ref(false);
const isSending = ref(false);
const isGoogleLoading = ref(false);
const agreeToTerms = ref(true);
const canAccessGoogle = ref(true);

const showEmailSign = computed({
  get: () => props.showEmail,
  set: (v) => emit('update:show-email', v),
});

const showPhoneSign = computed({
  get: () => props.showPhone,
  set: (v) => emit('update:show-phone', v),
});
const isVerifyingPhone = ref(false);
const smsCodeSent = ref(false);
const smsCountdown = ref(0);
const smsTimer = ref(null);
const showInviteCode = ref(false);

// Single source of truth for current method based on visible form
const currentMethod = computed(() => (showPhoneSign.value ? 'phone' : 'email'));

const formState = ref({
  email: '',
  phone: '',
  countryCode: '+86',
  smsCode: '',
  emailCode: '',
  password: '',
  confirmPassword: '',
  inviteCode: route.query.inviteCode || '',
});

// 如果URL携带了邀请码，默认展开邀请码输入框
if (route.query.inviteCode) {
  showInviteCode.value = true;
}

const randonKey = Date.now();

// Check Google accessibility with fast multi-round detection
const checkGoogleAccess = async () => {
  let attempts = 0;
  const maxAttempts = 3;

  const testGoogleAccess = () => {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(false);
      }, 500); // Fast 300ms timeout per attempt

      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };

      img.onerror = (e) => {
        console.log(e);
        clearTimeout(timeout);
        resolve(false);
      };

      // Add cache busting with timestamp and random number to prevent caching
      img.src = `https://www.google.com/favicon.ico?nocache=${randonKey}`;
    });
  };

  // Multi-round detection
  while (attempts < maxAttempts) {
    attempts++;

    try {
      const isAccessible = await testGoogleAccess();

      if (isAccessible) {
        canAccessGoogle.value = true;
        return;
      }

      // If this is not the last attempt, wait briefly before retry
      if (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch {
      // Continue to next attempt
    }
  }

  // All attempts failed
  canAccessGoogle.value = false;
  showEmailSign.value = true;
};

onMounted(() => {
  checkGoogleAccess();
  startPeriodicGoogleCheck();
});

// Periodic check for Google accessibility
const startPeriodicGoogleCheck = () => {
  // Clear any existing interval
  if (googleCheckInterval.value) {
    clearInterval(googleCheckInterval.value);
  }

  // Set up periodic check every 30 seconds
  googleCheckInterval.value = setInterval(() => {
    // Only check if Google is currently not accessible
    if (!canAccessGoogle.value) {
      checkGoogleAccess();
    }
  }, 15 * 1000); // Check every 30 seconds
};

const googleCheckInterval = ref(null);

// Clean up interval when component unmounts
onUnmounted(() => {
  if (googleCheckInterval.value) {
    clearInterval(googleCheckInterval.value);
  }
  if (smsTimer.value) {
    clearInterval(smsTimer.value);
  }
  if (emailTimer.value) {
    clearInterval(emailTimer.value);
  }
});

function handleShowEmail() {
  showEmailSign.value = true;
  showPhoneSign.value = false;
  emit('update:show-email', true);
  emit('update:show-phone', false);
  nextTick(() => {
    if (emailInputRef.value) {
      emailInputRef.value.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      emailInputRef.value.focus();
    }
    setTimeout(() => {
      emit('scroll-to-bottom');
    }, 100);
  });
}

function handleShowPhone() {
  showPhoneSign.value = true;
  showEmailSign.value = false;
  emit('update:show-email', false);
  emit('update:show-phone', true);
  nextTick(() => {
    if (phoneInputRef.value) {
      phoneInputRef.value.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      phoneInputRef.value.focus();
    }
    setTimeout(() => {
      emit('scroll-to-bottom');
    }, 100);
  });
}

function toggleInviteCode() {
  showInviteCode.value = !showInviteCode.value;
  if (showInviteCode.value) {
    nextTick(() => {
      emit('scroll-to-bottom');
    });
  }
}

function get(key, v) {
  return i18n.get(key, v);
}

function afterAuth(token) {
  setToken(token);
  props.afterAuth && props.afterAuth(token);
}

const emailCodeSent = ref(false);
const emailCountdown = ref(0);
const emailTimer = ref(null);

// Reset helpers to avoid cross-mode state bleed
function resetEmailVerification() {
  emailCodeSent.value = false;
  emailCountdown.value = 0;
  if (emailTimer.value) {
    clearInterval(emailTimer.value);
    emailTimer.value = null;
  }
}

function resetSmsVerification() {
  smsCodeSent.value = false;
  smsCountdown.value = 0;
  if (smsTimer.value) {
    clearInterval(smsTimer.value);
    smsTimer.value = null;
  }
}

// When switching between email/phone forms, clear opposite verification state
watch(showEmailSign, (now, prev) => {
  if (now && !prev) {
    resetSmsVerification();
  }
});

watch(showPhoneSign, (now, prev) => {
  if (now && !prev) {
    resetEmailVerification();
  }
});

// When toggling login/sign-up, clear verification specific states and confirm password
watch(isLogin, (now) => {
  resetEmailVerification();
  resetSmsVerification();
  if (now) {
    showInviteCode.value = false;
  }
  formState.value.confirmPassword = '';
});

const sendEmailCode = async () => {
  if (!formState.value.email) {
    toast({
      title: get('signIn.emailRequired'),
    });
    return;
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formState.value.email)) {
    toast({
      title: get('signIn.invalidEmail'),
    });
    return;
  }

  isSending.value = true;
  try {
    const api = await getAPI();
    const res = await fetch(`${api}/account/send-email-verification`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        email: formState.value.email,
        language: i18n.language || 'en',
      }),
    });

    if (res.ok) {
      emailCodeSent.value = true;
      emailCountdown.value = 60;
      emailTimer.value = setInterval(() => {
        emailCountdown.value--;
        if (emailCountdown.value <= 0) {
          clearInterval(emailTimer.value);
          emailTimer.value = null;
        }
      }, 1000);
      toast({
        title: get('signIn.emailCodeSent'),
        variant: 'success',
      });
    } else {
      const error = await res.json();
      toast({
        title: get(`signIn.emailMessages.${error.code}`, error.message),
        variant: 'warning',
      });
    }
  } catch (error) {
    console.error('Email verification send error:', error);
    toast({
      title: get('signIn.networkError'),
      variant: 'warning',
    });
  } finally {
    isSending.value = false;
  }
};

const sendSmsCode = async () => {
  if (!formState.value.phone) {
    toast({
      title: get('signIn.phoneRequired'),
    });
    return;
  }

  isVerifyingPhone.value = true;
  try {
    const api = await getAPI();
    const res = await fetch(`${api}/send-sms-code`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        phone: formState.value.phone,
        country_code: formState.value.countryCode,
      }),
    });

    if (res.ok) {
      smsCodeSent.value = true;
      smsCountdown.value = 60;
      smsTimer.value = setInterval(() => {
        smsCountdown.value--;
        if (smsCountdown.value <= 0) {
          clearInterval(smsTimer.value);
          smsTimer.value = null;
        }
      }, 1000);
      toast({
        title: get('signIn.smsCodeSent'),
      });
    } else {
      const error = await res.json();
      toast({
        title: get(`signIn.smsMessages.${error.code}`, error.message),
      });
    }
  } catch (error) {
    console.error('SMS send error:', error);
    toast({
      title: get('signIn.networkError'),
    });
  } finally {
    isVerifyingPhone.value = false;
  }
};

const handleSubmit = async () => {
  if (!isLogin.value && !agreeToTerms.value) {
    toast({
      title: get('signIn.agreeToTermsRequired'),
    });
    return;
  }
  if (isLoading.value) return;

  isLoading.value = true;
  try {
    if (isLogin.value) {
      const api = await getAPI();
      const loginData = {
        password: formState.value.password,
        env: (await window.api?.getEnv?.()) || 'web',
      };

      // 根据当前登录方式添加对应的字段
      if (currentMethod.value === 'email') {
        loginData.email = formState.value.email;
      } else {
        loginData.phone = formState.value.phone;
        loginData.country_code = formState.value.countryCode;
      }

      const res = await fetch(`${api}/sign-in`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      if (res.ok) {
        const body = await res.json();
        const { token } = body;
        afterAuth(token);
      } else {
        const error = await res.json();
        toast({
          title: get('signIn.loginFailed'),
        });
        console.error('Login error:', error);
      }
    } else {
      if (formState.value.password !== formState.value.confirmPassword) {
        toast({
          title: get('signIn.passwordMismatch'),
        });
        return;
      }

      const api = await getAPI();
      const signUpData = {
        password: formState.value.password,
        inviteCode: formState.value.inviteCode,
        env: (await window.api?.getEnv?.()) || 'web',
        language: i18n.language,
      };

      if (currentMethod.value === 'email') {
        signUpData.email = formState.value.email;
        signUpData.emailCode = formState.value.emailCode;
      } else {
        signUpData.phone = formState.value.phone;
        signUpData.smsCode = formState.value.smsCode;
        signUpData.country_code = formState.value.countryCode;
      }

      const res = await fetch(`${api}/sign-up`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(signUpData),
      });

      if (res.ok) {
        const body = await res.json();
        const { token } = body;
        afterAuth(token);
      } else {
        const error = await res.json();
        toast({
          title: get(`signIn.messages.${error.code}`, error.message),
        });
        console.error('Registration error:', error);
      }
    }
  } catch (error) {
    console.error('Login error:', error);
    toast({
      title: get('signIn.networkError'),
    });
  } finally {
    isLoading.value = false;
  }
};

const handleGoogleLogin = async (formId) => {
  isGoogleLoading.value = true;
  const fallbackId = 'google-form';
  const id = formId || fallbackId;
  const form = document.getElementById(id);
  form && form.submit();
};
</script>
<template>
  <div :class="cn('relative transition-all duration-300', props.class)">
    <!-- Header -->
    <div class="p-0">
      <div v-if="!showCase" class="text-center mb-8">
        <h1 class="text-[1.75rem] font-light text-black mb-2">
          {{ isLogin ? $t('signIn.signIn') : $t('signIn.createYourAccount') }}
        </h1>
      </div>

      <!-- Social Login -->
      <div v-if="!showEmailSign && !showPhoneSign" class="space-y-3">
        <div class="relative">
          <form
            v-if="canAccessGoogle"
            id="google-form"
            :action="`${getAPI()}/auth/signin/google?inviteCode=${formState.inviteCode}`"
            method="POST"
            class="w-full"
          >
            <input
              type="hidden"
              name="callbackUrl"
              :value="`${getAPI()}?inviteCode=${formState.inviteCode}`"
            />
            <button
              type="button"
              @click="handleGoogleLogin('google-form')"
              :disabled="isGoogleLoading"
              class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
              style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
            >
              <template v-if="!isGoogleLoading">
                <svg
                  class="w-5 h-5 absolute left-4 transition-transform group-hover:scale-105"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <span class="text-black/80 font-light">{{ $t('signIn.continueWith') }} Google</span>
              </template>
              <template v-else>
                <Loader2 class="animate-spin h-5 w-5 absolute left-4 text-black/60" />
                <span class="text-black/60 font-light">Connecting...</span>
              </template>
            </button>
          </form>

          <!-- Google Unavailable Message -->
          <div v-if="!canAccessGoogle" class="w-full">
            <button
              type="button"
              disabled
              class="w-full flex items-center justify-center gap-3 bg-black/5 border border-black/5 px-6 py-3 rounded-[12px] font-medium cursor-not-allowed opacity-60"
            >
              <svg class="w-5 h-5 opacity-60" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#9CA3AF"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#9CA3AF"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#9CA3AF"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span class="text-black/40 font-light">{{ $t('signIn.googleUnavailable') }}</span>
            </button>
          </div>
        </div>

        <!-- Divider -->
        <div v-if="!showEmailSign && !showPhoneSign" class="relative my-6 flex items-center">
          <div class="flex-grow border-t border-black/5"></div>
          <span
            class="px-4 text-xs text-black/40 bg-transparent font-light uppercase tracking-wider"
            >or</span
          >
          <div class="flex-grow border-t border-black/5"></div>
        </div>

        <!-- Email and Phone Sign Options -->
        <div v-if="!showEmailSign && !showPhoneSign" class="space-y-3">
          <button
            type="button"
            @click="handleShowEmail"
            class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
            style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
          >
            <Mail
              class="w-5 h-5 absolute left-4 text-black/60 transition-transform group-hover:scale-105"
            />
            <span class="text-black/80 font-light">{{ $t('signIn.continueWithEmail') }}</span>
          </button>

          <button
            type="button"
            @click="handleShowPhone"
            class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
            style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
          >
            <Phone
              class="w-5 h-5 absolute left-4 text-black/60 transition-transform group-hover:scale-105"
            />
            <span class="text-black/80 font-light">{{ $t('signIn.continueWithPhone') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Email Form -->
    <form v-if="showEmailSign" @submit.prevent="handleSubmit" class="p-0 mt-4 space-y-2">
      <!-- Email Input -->
      <Input
        ref="emailInputRef"
        v-model="formState.email"
        type="email"
        name="email"
        icon="mail"
        :label="$t('signIn.email')"
        :placeholder="$t('signIn.emailPlaceholder')"
        required
      />

      <!-- Email Code Input for Email Registration -->
      <div v-if="!isLogin && currentMethod === 'email'" class="space-y-2">
        <label class="text-sm font-light text-black/60">{{ $t('signIn.emailCode') }}</label>
        <div class="flex gap-2">
          <div class="relative flex-1">
            <input
              v-model="formState.emailCode"
              type="text"
              maxlength="6"
              required
              class="w-full px-4 py-2.5 border border-black/10 rounded-[12px] text-black placeholder-black/30 focus:ring-2 focus:ring-black/10 focus:border-black/20 focus:outline-none transition-all duration-300 bg-white/70 backdrop-blur-sm font-light"
              :placeholder="$t('signIn.emailCodePlaceholder')"
            />
          </div>
          <button
            type="button"
            @click="sendEmailCode"
            :disabled="isSending || emailCountdown > 0 || !formState.email"
            class="px-5 py-2.5 bg-black/90 hover:bg-black text-white rounded-[12px] font-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <template v-if="isSending">
              <Loader2 class="animate-spin h-4 w-4" />
            </template>
            <template v-else-if="emailCountdown > 0"> {{ emailCountdown }}s </template>
            <template v-else>
              {{ emailCodeSent ? $t('signIn.resendCode') : $t('signIn.sendCode') }}
            </template>
          </button>
        </div>
      </div>

      <!-- Password Input -->
      <Input
        v-model="formState.password"
        type="password"
        icon="lock"
        name="password"
        :label="$t('signIn.password')"
        :placeholder="$t('signIn.passwordPlaceholder')"
        :show-password-toggle="true"
        required
      />

      <!-- Confirm Password for Sign Up -->
      <Input
        v-if="!isLogin && formState.password"
        v-model="formState.confirmPassword"
        type="password"
        name="confirmPassword"
        icon="lock"
        :label="$t('signIn.confirmPassword')"
        :placeholder="$t('signIn.confirmPasswordPlaceholder')"
        :show-password-toggle="true"
        required
      />

      <!-- Invite Code Toggle Button for Sign Up -->
      <div v-if="!isLogin && !showInviteCode" class="text-right">
        <button
          type="button"
          @click="toggleInviteCode"
          class="text-sm text-black/60 hover:text-black transition-colors underline decoration-dotted underline-offset-2"
        >
          {{ $t('signIn.haveInviteCode') }}
        </button>
      </div>

      <!-- Invite Code Input for Sign Up (Collapsible) -->
      <Input
        v-if="!isLogin && showInviteCode"
        v-model="formState.inviteCode"
        type="text"
        name="ticket"
        icon="ticket"
        :label="$t('signIn.inviteCode')"
        :placeholder="$t('signIn.inviteCodePlaceholder')"
      />

      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full h-11 flex items-center justify-center bg-black/90 hover:bg-black text-white px-4 rounded-[12px] font-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
      >
        <span v-if="!isLoading">
          {{ isLogin ? $t('signIn.loginButton') : $t('signIn.registerButton') }}
        </span>
        <span v-else class="flex items-center justify-center">
          <Loader2 class="animate-spin h-5 w-5 text-white" />
        </span>
      </button>

      <!-- Switch Mode & Forgot Password -->
      <div class="text-center space-y-2 pt-4">
        <button
          @click.prevent.stop="isLogin = !isLogin"
          class="text-black/60 hover:text-black transition-colors text-sm"
        >
          {{ isLogin ? $t('signIn.noAccount') : $t('signIn.hasAccount') }}
        </button>

        <div v-if="isLogin">
          <router-link
            :to="{ name: 'AccountsReset' }"
            class="text-black/60 hover:text-black transition-colors text-sm"
          >
            {{ $t('signIn.forgotPassword') }}
          </router-link>
        </div>
      </div>

      <!-- Divider -->
      <div class="relative my-6 flex items-center">
        <div class="flex-grow border-t border-black/5"></div>
        <span class="px-4 text-xs text-black/40 bg-transparent font-light uppercase tracking-wider"
          >or</span
        >
        <div class="flex-grow border-t border-black/5"></div>
      </div>

      <!-- Alternative Sign Options -->
      <div class="space-y-2">
        <!-- Google Button -->
        <form
          v-if="canAccessGoogle"
          id="google-form-alt"
          :action="`${getAPI()}/auth/signin/google?inviteCode=${formState.inviteCode}`"
          method="POST"
          class="w-full"
        >
          <input
            type="hidden"
            name="callbackUrl"
            :value="`${getAPI()}?inviteCode=${formState.inviteCode}`"
          />
          <button
            type="button"
            @click="handleGoogleLogin('google-form-alt')"
            :disabled="isGoogleLoading"
            class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
            style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
          >
            <template v-if="!isGoogleLoading">
              <svg
                class="w-5 h-5 absolute left-4 transition-transform group-hover:scale-105"
                viewBox="0 0 24 24"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span class="text-black/80 font-light">{{ $t('signIn.continueWith') }} Google</span>
            </template>
            <template v-else>
              <Loader2 class="animate-spin h-5 w-5 absolute left-4 text-black/60" />
              <span class="text-black/60 font-light">Connecting...</span>
            </template>
          </button>
        </form>

        <!-- Phone Button -->
        <button
          type="button"
          @click="handleShowPhone"
          class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
          style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
        >
          <Phone
            class="w-5 h-5 absolute left-4 text-black/60 transition-transform group-hover:scale-105"
          />
          <span class="text-black/80 font-light">{{ $t('signIn.continueWithPhone') }}</span>
        </button>
      </div>
    </form>

    <!-- Phone Form -->
    <form v-if="showPhoneSign" @submit.prevent="handleSubmit" class="p-0 mt-4 space-y-2">
      <!-- Phone Input -->
      <div class="space-y-2">
        <div class="flex gap-2">
          <!-- Country Code Selector -->
          <select
            v-model="formState.countryCode"
            class="w-[120px] flex-shrink-0 px-4 py-2.5 border border-black/10 rounded-[12px] text-black focus:ring-2 focus:ring-black/10 focus:border-black/20 focus:outline-none transition-all duration-300 bg-white/70 backdrop-blur-sm font-light"
          >
            <option v-for="country in countryCodes" :key="country.code" :value="country.code">
              {{ country.flag }} {{ country.code }}
            </option>
          </select>
          <!-- Phone Number Input -->
          <div class="flex-1">
            <Input
              ref="phoneInputRef"
              v-model="formState.phone"
              type="tel"
              icon="phone"
              :placeholder="$t('signIn.phonePlaceholder')"
              autocomplete="username"
              name="phone"
              required
            />
          </div>
        </div>
      </div>

      <!-- SMS Code Input for Phone Registration -->
      <div v-if="!isLogin" class="space-y-2">
        <div class="flex gap-2">
          <div class="relative flex-1">
            <input
              v-model="formState.smsCode"
              type="text"
              maxlength="6"
              required
              class="w-full px-4 py-2.5 border border-black/10 rounded-[12px] text-black placeholder-black/30 focus:ring-2 focus:ring-black/10 focus:border-black/20 focus:outline-none transition-all duration-300 bg-white/70 backdrop-blur-sm font-light"
              :placeholder="$t('signIn.smsCodePlaceholder')"
            />
          </div>
          <button
            type="button"
            @click="sendSmsCode"
            :disabled="isVerifyingPhone || smsCountdown > 0 || !formState.phone"
            class="px-5 py-2.5 bg-black/90 hover:bg-black text-white rounded-[12px] font-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <template v-if="isVerifyingPhone">
              <Loader2 class="animate-spin h-4 w-4" />
            </template>
            <template v-else-if="smsCountdown > 0"> {{ smsCountdown }}s </template>
            <template v-else>
              {{ smsCodeSent ? $t('signIn.resendCode') : $t('signIn.sendCode') }}
            </template>
          </button>
        </div>
      </div>

      <!-- Password Input -->
      <Input
        v-model="formState.password"
        type="password"
        icon="lock"
        :label="$t('signIn.password')"
        :placeholder="$t('signIn.passwordPlaceholder')"
        :show-password-toggle="true"
        required
      />

      <!-- Confirm Password for Sign Up -->
      <Input
        v-if="!isLogin && formState.password"
        v-model="formState.confirmPassword"
        type="password"
        icon="lock"
        :label="$t('signIn.confirmPassword')"
        :placeholder="$t('signIn.confirmPasswordPlaceholder')"
        :show-password-toggle="true"
        required
      />

      <!-- Invite Code Toggle Button for Sign Up -->
      <div v-if="!isLogin && !showInviteCode" class="text-right">
        <button
          type="button"
          @click="toggleInviteCode"
          class="text-sm text-black/60 hover:text-black transition-colors underline decoration-dotted underline-offset-2"
        >
          {{ $t('signIn.haveInviteCode') }}
        </button>
      </div>

      <!-- Invite Code Input for Sign Up (Collapsible) -->
      <Input
        v-if="!isLogin && showInviteCode"
        v-model="formState.inviteCode"
        type="text"
        icon="ticket"
        :label="$t('signIn.inviteCode')"
        :placeholder="$t('signIn.inviteCodePlaceholder')"
      />

      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full h-11 flex items-center justify-center bg-black/90 hover:bg-black text-white px-4 rounded-[12px] font-light transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
      >
        <span v-if="!isLoading">
          {{ isLogin ? $t('signIn.loginButton') : $t('signIn.registerButton') }}
        </span>
        <span v-else class="flex items-center justify-center">
          <Loader2 class="animate-spin h-5 w-5 text-white" />
        </span>
      </button>

      <!-- Switch Mode & Forgot Password -->
      <div class="text-center space-y-2 pt-4">
        <button
          @click.prevent.stop="isLogin = !isLogin"
          class="text-black/60 hover:text-black transition-colors text-sm"
        >
          {{ isLogin ? $t('signIn.noAccount') : $t('signIn.hasAccount') }}
        </button>

        <div v-if="isLogin">
          <router-link
            :to="{ name: 'AccountsReset' }"
            class="text-black/60 hover:text-black transition-colors text-sm"
          >
            {{ $t('signIn.forgotPassword') }}
          </router-link>
        </div>
      </div>

      <!-- Divider -->
      <div class="relative my-6 flex items-center">
        <div class="flex-grow border-t border-black/5"></div>
        <span class="px-4 text-xs text-black/40 bg-transparent font-light uppercase tracking-wider"
          >or</span
        >
        <div class="flex-grow border-t border-black/5"></div>
      </div>

      <!-- Alternative Sign Options -->
      <div class="space-y-2">
        <!-- Google Button -->
        <form
          v-if="canAccessGoogle"
          id="google-form-alt2"
          :action="`${getAPI()}/auth/signin/google?inviteCode=${formState.inviteCode}`"
          method="POST"
          class="w-full"
        >
          <input
            type="hidden"
            name="callbackUrl"
            :value="`${getAPI()}?inviteCode=${formState.inviteCode}`"
          />
          <button
            type="button"
            @click="handleGoogleLogin('google-form-alt2')"
            :disabled="isGoogleLoading"
            class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
            style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
          >
            <template v-if="!isGoogleLoading">
              <svg
                class="w-5 h-5 absolute left-4 transition-transform group-hover:scale-105"
                viewBox="0 0 24 24"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span class="text-black/80 font-light">{{ $t('signIn.continueWith') }} Google</span>
            </template>
            <template v-else>
              <Loader2 class="animate-spin h-5 w-5 absolute left-4 text-black/60" />
              <span class="text-black/60 font-light">Connecting...</span>
            </template>
          </button>
        </form>

        <!-- Email Button -->
        <button
          type="button"
          @click="handleShowEmail"
          class="w-full relative flex items-center justify-center px-6 py-3 rounded-[12px] font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group hover:shadow-lg"
          style="background-color: rgba(255, 255, 255, 1); border: 1px solid rgba(0, 0, 0, 0.2)"
        >
          <Mail
            class="w-5 h-5 absolute left-4 text-black/60 transition-transform group-hover:scale-105"
          />
          <span class="text-black/80 font-light">{{ $t('signIn.continueWithEmail') }}</span>
        </button>
      </div>
    </form>

    <!-- Terms & Privacy -->
    <div v-if="!showEmailSign && !showPhoneSign" class="text-center text-xs text-black/40 pt-8">
      <div class="p-0 font-light">
        {{ $t('signIn.agreeToTerms') }}
        <a
          href="/terms"
          class="text-black/60 hover:text-black font-light transition-colors underline"
          >{{ $t('signIn.terms') }}</a
        >
        {{ $t('signIn.and') }}
        <a
          href="/privacy"
          class="text-black/60 hover:text-black font-light transition-colors underline"
          >{{ $t('signIn.privacy') }}</a
        >
      </div>
    </div>
  </div>
</template>
